import type { ApiRenterNotification, ApiRenterNotificationPayload } from 'src/types/notification';

import { RenterNotificationType } from 'src/types/notification';

import { formatUtcDay } from './date';
import { formatAmount as moneyAmount } from './money';

/**
 * Cabinet copy of the renter notification feed (spec §5). Deliberately the same approved
 * drafts the api renders into the email leg (`renderRenterNotificationEmail`), so the two
 * channels the renter has — cabinet and email — never say different things about the same
 * event. (Telegram for renters was postponed: it needs its own bot.)
 *
 * Pure, and unit-tested: it runs on payloads written by whatever api version was deployed
 * when the event happened, so every field is optional and a missing one degrades to a
 * placeholder instead of throwing on a screen the renter is reading.
 */

export interface RenterNotificationCopy {
  title: string;
  body: string;
}

/**
 * Money is pinned to en-US, never the browser locale: a ru/de visitor would otherwise read
 * "1 234,5 USDT" for an amount the platform states in English everywhere else.
 */
const formatAmount = (value: unknown): string => {
  const numeric = Number(value);

  if (!Number.isFinite(numeric)) {
    return '—';
  }

  return moneyAmount(numeric, 6);
};

const formatDate = (value: unknown): string => {
  if (!value) {
    return '—';
  }

  // The payload dates are UTC-midnight ISO moments naming a booking day, so the day is
  // read in UTC — a Los Angeles renter must not be told a Dec 1 booking was Nov 30. This
  // also keeps the copy identical to the email leg, which the api renders in UTC.
  return formatUtcDay(value as string);
};

const carOf = (payload: ApiRenterNotificationPayload): string =>
  [payload.make, payload.model].filter(Boolean).join(' ') || 'your vehicle';

const periodOf = (payload: ApiRenterNotificationPayload): string =>
  `${formatDate(payload.startDate)}–${formatDate(payload.endDate)}`;

export const renterNotificationCopy = (
  notification: Pick<ApiRenterNotification, 'type' | 'payload'>
): RenterNotificationCopy => {
  const payload = notification.payload ?? {};
  const car = carOf(payload);
  const period = periodOf(payload);
  // An amount in the token the payload names. A payload that names none says the amount alone
  // rather than guess: the guess used to be USDT, which no new rental settles in (one-rail spec §0).
  const money = (value: unknown): string =>
    [formatAmount(value), payload.currency].filter(Boolean).join(' ');

  switch (notification.type) {
    case RenterNotificationType.RENTAL_STARTED:
      return {
        title: 'Rental started',
        body: `Your rental has started: ${car}, ${period}. Deposit ${money(payload.depositAmount)} is held by the smart contract until return and inspection.`,
      };
    case RenterNotificationType.RENTAL_ENDING_24H:
      return {
        title: 'Rental ends in 24 hours',
        body: `Your rental of ${car} (${period}) ends in 24 hours. Extend now to keep your current daily rate. After the end date extension requires a full deposit, and every started day is charged from your deposit at the 1–3 day rate (${money(payload.dailyOverdueRate)}/day).`,
      };
    case RenterNotificationType.RENTAL_OVERDUE_CHARGED:
      return {
        title: `Overdue day ${payload.day ?? '—'} charged`,
        body: `Overdue day ${payload.day ?? '—'}: ${money(payload.chargedAmount)} charged from your deposit (${money(payload.remainingDeposit)} left). Return the car, or top up your deposit and extend to switch back to your tier rate.`,
      };
    case RenterNotificationType.RENTAL_DEPOSIT_EXHAUSTED:
      return {
        title: 'Deposit exhausted',
        body: `Your deposit on ${car} is exhausted. Return the vehicle immediately; the dealer may now escalate to the authorities.`,
      };
    case RenterNotificationType.RENTAL_DEPOSIT_RELEASED:
      return {
        title: 'Deposit returned',
        body: `Inspection complete — no defects reported for ${car} (${period}). Your deposit ${money(payload.amount)} has been returned to your wallet.`,
      };
    case RenterNotificationType.RENTAL_BOOKING_EXPIRED:
      return {
        title: 'Booking cancelled',
        body: `Your booking of ${car} (${period}) was cancelled: payment was not received within 30 minutes. The dates are available again.`,
      };
    default:
      // A newer api emitting an event this build predates: show what is knowable rather
      // than an empty row.
      return {
        title: notification.type,
        body: `${car} (${period}).`,
      };
  }
};
